import { signal } from '@preact/signals'
import { render } from 'preact'
import { initialViewState, text } from '#contract'
import type { GameClient, StringTable, ViewState } from '#contract'
import { App } from './app/app.tsx'
import type { ClientBridge } from './app/client-context.ts'
import { en } from './i18n/en.ts'
import { createI18n } from './i18n/index.ts'
import { applyTheme } from './theme/index.ts'

export interface UiHandle {
  /** Connects the game client and adds the content's strings to the UI's own. Call once. */
  attach(client: GameClient, strings: StringTable): void
  /** Shows the error screen when there's no client to report it, e.g. the engine chunk failed to load. */
  fail(error: unknown): void
}

/** Renders the UI at once, so the boot screen paints before the engine chunk arrives. */
export function mountUi(root: HTMLElement): UiHandle {
  applyTheme('gallery')
  const view = signal<ViewState>(initialViewState)
  const draw = (bridge: ClientBridge, strings: StringTable = {}): void => {
    render(<App bridge={bridge} i18n={createI18n([en, strings])} />, root)
  }
  draw({ client: null, view })

  return {
    attach(client, strings) {
      view.value = client.view.get()
      // The client lives as long as the page, so this subscription is never removed.
      client.view.subscribe((state) => {
        view.value = state
      })
      draw({ client, view }, strings)
    },
    fail(error) {
      view.value = {
        ...view.peek(),
        phase: 'error',
        error: {
          title: text('error.loadFailed'),
          detail: error instanceof Error ? error.message : String(error),
        },
      }
    },
  }
}
